import { FormEvent, useEffect, useRef, useState } from "react";
import { AudioLines, KeyRound, Link2, Mail, ShieldCheck, X } from "lucide-react";
import { DictaLogo } from "./DictaLogo";
import { beginAuth } from "../app/account";
import { sendEntryTelemetry } from "../app/telemetry";

type SignInProps = {
  onAuthenticated: (email: string) => void;
  onTrySample?: () => void;
  onOpenWorkspace?: () => void;
  onSignOut?: () => void;
  authenticated?: boolean;
  initialMode?: "signin" | "signup";
  initialOpen?: boolean;
  remote?: boolean;
};

export function SignIn({
  onAuthenticated,
  onTrySample,
  onOpenWorkspace,
  onSignOut,
  authenticated = false,
  initialMode = "signin",
  initialOpen = false,
  remote = false
}: SignInProps) {
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [step, setStep] = useState<"email" | "code">("email");
  const [mode, setMode] = useState<"signin" | "signup">(initialMode);
  const [authOpen, setAuthOpen] = useState(
    !remote && (initialOpen || initialMode === "signup")
  );
  const [state, setState] = useState<"idle" | "loading" | "error">("idle");
  const [message, setMessage] = useState("");
  const dialogRef = useRef<HTMLDialogElement | null>(null);
  useEffect(() => {
    sendEntryTelemetry("client.entry.viewed");
  }, []);

  useEffect(() => {
    setMode(initialMode);
    setStep("email");
    if (remote && (initialOpen || initialMode === "signup")) {
      beginAuth(initialMode);
      return;
    }
    if (initialOpen || initialMode === "signup") setAuthOpen(true);
  }, [initialMode, initialOpen, remote]);

  useEffect(() => {
    if (!authOpen) return;
    const dialog = dialogRef.current;
    if (!dialog || dialog.open) return;
    if (typeof dialog.showModal === "function") {
      dialog.showModal();
    } else {
      dialog.setAttribute("open", "");
    }
    dialog.querySelector<HTMLInputElement>('input[type="email"]')?.focus();
  }, [authOpen]);

  useEffect(() => {
    if (!remote || authenticated) return;
    const url = new URL(window.location.href);
    const authError = url.searchParams.get("auth_error");
    if (authError) {
      const authRequestId = url.searchParams.get("auth_request_id");
      const messages: Record<string, string> = {
        auth_cancelled: "Sign-in was cancelled.",
        auth_failed: "Sign-in could not be verified. Please try again.",
        auth_unavailable: "Sign-in is not configured for this environment yet.",
        email_unverified: "Verify your email address before entering Dicta.",
        method_unavailable: "That sign-in method is unavailable.",
        account_conflict: "This verified identity could not be safely linked."
      };
      const reference =
        authRequestId && /^[A-Za-z0-9-]{8,128}$/.test(authRequestId)
          ? ` Request ${authRequestId}.`
          : "";
      setState("error");
      setMessage(
        `${messages[authError] ?? "Provider sign-in was not accepted."}${reference}`
      );
      url.searchParams.delete("auth_error");
      url.searchParams.delete("auth_request_id");
      window.history.replaceState({}, "", `${url.pathname}${url.search}${url.hash}`);
    }
  }, [authenticated, remote]);

  const requestCode = (event: FormEvent) => {
    event.preventDefault();
    if (!email.includes("@")) {
      setState("error");
      setMessage("Enter a valid work email.");
      return;
    }
    setState("loading");
    window.setTimeout(() => {
      setStep("code");
      setState("idle");
    }, 450);
  };

  const verifyCode = async (event: FormEvent) => {
    event.preventDefault();
    if (code !== "246810") {
      setState("error");
      setMessage("That code was not recognised. For this local build, use 246810.");
      return;
    }
    setState("loading");
    window.setTimeout(() => {
      setAuthOpen(false);
      onAuthenticated(email);
    }, 350);
  };

  const openAuth = (nextMode: "signin" | "signup") => {
    if (nextMode === "signin") {
      sendEntryTelemetry("client.entry.signin_selected");
    }
    if (remote) {
      beginAuth(nextMode, { returnTo: "/" });
      return;
    }
    setMode(nextMode);
    setStep("email");
    setState("idle");
    setMessage("");
    setCode("");
    setAuthOpen(true);
  };

  return (
    <main id="main-content" className="signin-shell" tabIndex={-1}>
      <a href="#main-content" className="skip-link">
        Skip to content
      </a>
      <header className="signin-nav">
        <div className="signin-brand-lockup">
          <div className="signin-wordmark">dicta</div>
          <span className="beta-badge">Beta</span>
        </div>
        <div className="signin-nav__actions" aria-label="Account">
          {authenticated ? (
            <>
              <button
                className="button button--primary"
                type="button"
                onClick={onOpenWorkspace}
              >
                Open transcripts
              </button>
              <button
                className="button button--text"
                type="button"
                onClick={onSignOut}
              >
                Sign out
              </button>
            </>
          ) : (
            <>
              <button
                className="button button--text"
                type="button"
                onClick={() => openAuth("signin")}
              >
                Log in
              </button>
              <button
                className="button button--primary"
                type="button"
                onClick={() => openAuth("signup")}
              >
                Create account
              </button>
            </>
          )}
        </div>
      </header>

      <section className="signin-intro" aria-labelledby="signin-title">
        <div className="signin-statement">
          <h1 id="signin-title">Transcribe your matter.</h1>
          <p className="signin-sub">So you can stay in the conversation.</p>
          {onTrySample && (
            <div className="signin-cta">
              <button
                className="button button--primary"
                type="button"
                aria-label="Try a sample recording"
                onClick={() => {
                  sendEntryTelemetry("client.entry.try_selected");
                  onTrySample();
                }}
              >
                Try a sample recording
              </button>
            </div>
          )}

          {remote && state === "error" && message && (
            <div className="signin-auth-error" role="alert">
              <p>{message}</p>
              <button
                className="button button--quiet"
                type="button"
                onClick={() => beginAuth(mode)}
              >
                Try sign-in again
              </button>
            </div>
          )}

          <ul className="signin-assurances" aria-label="Dicta safeguards">
            <li>
              <ShieldCheck size={18} aria-hidden="true" />
              <span>Confirm permission before you record.</span>
            </li>
            <li>
              <AudioLines size={18} aria-hidden="true" />
              <span>Check every word against the source audio.</span>
            </li>
            <li>
              <Link2 size={18} aria-hidden="true" />
              <span>Share the finished transcript with a secure link.</span>
            </li>
          </ul>
        </div>

        <div className="signin-visual">
          {onTrySample ? (
            <button
              className="signin-demo-art"
              type="button"
              onClick={() => {
                sendEntryTelemetry("client.entry.try_selected");
                onTrySample();
              }}
              aria-label="Try a sample recording"
            >
              <DictaLogo interactive />
            </button>
          ) : (
            <DictaLogo />
          )}
          {onTrySample && (
            <p className="signin-visual__caption">
              Click the dictaphone to see it in action.
            </p>
          )}
        </div>
      </section>

      <footer className="signin-footer">
        <span>dicta · Legal dictation</span>
        <span className="signin-footer__links">
          <a href="/faq">FAQ</a>
          <a href="/terms">Terms</a>
          <a href="/privacy">Privacy</a>
          <a href="mailto:info@dicta.io">Contact</a>
          <a href="/privacy#security">Security</a>
        </span>
      </footer>

      {authOpen && (
        <dialog
          ref={dialogRef}
          className="signin-dialog"
          aria-labelledby="signin-dialog-title"
          onCancel={(event) => {
            event.preventDefault();
            setAuthOpen(false);
          }}
          onClick={(event) => {
            if (event.target === event.currentTarget) setAuthOpen(false);
          }}
        >
          <section id="signin-panel" className="signin-panel" aria-label="Secure sign in">
            <div className="signin-panel__head">
              <h2 id="signin-dialog-title">
                {mode === "signin" ? "Sign in" : "Create your account"}
              </h2>
              <button
                className="icon-button signin-dialog__close"
                type="button"
                onClick={() => setAuthOpen(false)}
                aria-label="Close sign in"
              >
                <X size={18} aria-hidden="true" />
              </button>
            </div>

            {step === "email" ? (
              <form className="form-stack" onSubmit={requestCode} noValidate>
                <label className="field">
                  <span>Email address</span>
                  <span className="input-wrap">
                    <Mail size={17} aria-hidden="true" />
                    <input
                      type="email"
                      value={email}
                      onChange={(event) => {
                        setEmail(event.target.value);
                        setState("idle");
                      }}
                      aria-invalid={state === "error"}
                      aria-describedby="email-help"
                      autoComplete="email"
                      autoFocus
                    />
                  </span>
                  <small
                    id="email-help"
                    className={state === "error" ? "field-error" : ""}
                  >
                    {state === "error" && message
                      ? message
                      : mode === "signup"
                        ? "Your workspace is created the first time you sign in."
                        : remote
                          ? "Use the email address associated with this account."
                          : "We’ll send a six-digit, single-use code."}
                  </small>
                </label>
                <button
                  className="button button--primary"
                  type="submit"
                  aria-busy={state === "loading"}
                  disabled={state === "loading"}
                >
                  {state === "loading"
                    ? "Continuing…"
                    : mode === "signup"
                      ? "Create account"
                      : remote
                        ? "Continue"
                        : "Send secure code"}
                </button>
                {!remote && (
                  <button
                    className="button button--quiet"
                    type="button"
                    onClick={() => {
                      setAuthOpen(false);
                      onAuthenticated(email || "counsel@dicta.demo");
                    }}
                    disabled={state === "loading"}
                  >
                    <KeyRound size={16} aria-hidden="true" />
                    Use passkey
                  </button>
                )}
              </form>
            ) : (
              <form className="form-stack" onSubmit={verifyCode} noValidate>
                <label className="field">
                  <span>{remote ? "Access code" : "Security code"}</span>
                  <input
                    className="code-input"
                    inputMode={remote ? "text" : "numeric"}
                    pattern={remote ? undefined : "[0-9]*"}
                    maxLength={remote ? 128 : 6}
                    value={code}
                    onChange={(event) => {
                      setCode(
                        remote
                          ? event.target.value
                          : event.target.value.replace(/\D/g, "")
                      );
                      setState("idle");
                      setMessage("");
                    }}
                    aria-invalid={state === "error"}
                    aria-describedby="code-help"
                    autoComplete={remote ? "current-password" : "one-time-code"}
                    autoFocus
                  />
                  <small
                    id="code-help"
                    className={state === "error" ? "field-error" : ""}
                  >
                    {state === "error" && message
                      ? message
                      : remote
                        ? "Access attempts are rate-limited and recorded in the audit log."
                        : "Local prototype code: 246810"}
                  </small>
                </label>
                <button
                  className="button button--primary"
                  type="submit"
                  aria-busy={state === "loading"}
                  disabled={state === "loading"}
                >
                  {state === "loading"
                    ? "Verifying…"
                    : remote
                      ? "Open workspace"
                      : "Open workspace"}
                </button>
                <button
                  className="button button--text"
                  type="button"
                  onClick={() => setStep("email")}
                  disabled={state === "loading"}
                >
                  Use another address
                </button>
              </form>
            )}

            {state === "error" && message && step === "email" && (
              <p className="form-message form-message--error" role="alert">
                {message}
              </p>
            )}

            <p className="signin-switch">
              {mode === "signin" ? "New to Dicta?" : "Already have an account?"}{" "}
              <button
                className="button button--text"
                type="button"
                onClick={() => {
                  setMode(mode === "signin" ? "signup" : "signin");
                  setStep("email");
                  setState("idle");
                  setMessage("");
                  setCode("");
                }}
              >
                {mode === "signin" ? "Create an account" : "Sign in"}
              </button>
            </p>

            <p className="signin-legal">
              By continuing you agree to our <a href="/terms">Terms of Use</a> and{" "}
              <a href="/privacy">Privacy Policy</a>.
            </p>
          </section>
        </dialog>
      )}
    </main>
  );
}
